// ============================================================================
// DATA TABLE — le rendu DOM du tableau riche de la charte (§3).
//
// ⚠️ ENTIÈREMENT CONTRÔLÉ : il ne trie pas, ne filtre pas, ne pagine pas, n'écrit dans AUCUN stockage.
// Il rend `rows` tel qu'on le lui donne et remonte les intentions. Motif : « toute liste est bornée
// côté serveur ; une liste sans pagination est un bug de conception » (règle transverse n° 10,
// volumétrie réelle jusqu'à ~2 M déclinaisons). Un tableau qui trierait tout seul aurait déjà chargé le
// jeu complet — il mentirait sur la volumétrie, et le mensonge serait invisible à la lecture de l'écran
// appelant. Le cas « liste courte déjà en mémoire » est servi par `useClientTable()`, un hook À PART.
//
// ⚠️ VRAI `<table>` + `<colgroup>` + `table-layout:fixed`, pas une grille CSS. Le `<colgroup>` rend le
// même service qu'une chaîne `grid-template-columns` partagée (une seule source de largeur, alignement
// en-tête↔cellules automatique) et donne la sémantique gratuitement — `<th scope>`, `aria-sort`. La
// maquette back-office n'a AUCUNE sémantique de tableau : c'est un manque à combler, pas à porter.
//
// ⚠️ AUCUNE COULEUR ÉCRITE ICI : que des classes de charte, dont les couleurs sont résolues par rôle
// (`TABLE_COLOR_ROLES`, consommé par le CSS de `ui-tokens/theme-web`). Un `var(--…)` inexistant ne
// provoque aucune erreur et casse en SILENCE (mesuré à 1,47:1 de contraste en thème sombre parce que
// `--teal-50` n'existait pas). `switch.tsx`, dans ce même paquet, consomme encore `--teal`, `--ink300`…
// : ce fichier ne doit pas servir de modèle.
// ⚠️ La phrase ci-dessus a été FAUSSE pendant une passe (la carte, le chargement, l'attente et l'échec
// posaient leurs couleurs en style en ligne) : six rôles ont été ajoutés et les six styles déplacés en
// CSS le 2026-08-16. Un commentaire qui décrit un état qu'on n'a pas est le même mensonge qu'un écran
// qui affiche un état qu'il n'a pas.
//
// ⚠️ TOUTES SES CLASSES VIVENT SOUS `.dt-root`, qu'il est SEUL à poser. Les recettes partent dans la
// couche de charte, donc dans toutes les apps ; la Console rend déjà `dt-row` sur ses propres `<tr>`.
// Sans racine, ce composant reprenait à distance le dessin d'un produit livré (cf. theme-web.ts).
// ============================================================================
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { rowHeightFor, TABLE_EXPAND_LEAD, type IconName } from '@orgazone/ui-tokens';
import {
  TABLE,
  TABLE_DEFAULTS,
  TABLE_DENSITY,
  TABLE_PAGE_SIZES,
  isColumnHideable,
  sortByPinnedRank,
  tableMinWidth,
  reconcileColumnLayout,
  type ColumnLayout,
  type TableAlign,
  type TableDensity,
} from '@orgazone/ui-tokens';
import { EmptyState } from './empty-state';
import { ColumnsButton } from './data-table-columns';
import { confirm } from './confirm';
import { alert } from './alert';
import { hasDialogHost } from './dialog';
import { key, t } from '@orgazone/i18n';

// ── COLONNES ───────────────────────────────────────────────────────────────────────────────────
export interface ColumnDef<Row> {
  /** Identité stable : clé de tri serveur, clé d'ordre, clé de persistance. */
  key: string;
  /** Intitulé affiché en en-tête ET repris devant la valeur en mode carte. */
  label: string;
  /** Contenu de la cellule. Reçoit la ligne entière : le tableau ignore la forme du domaine. */
  render: (row: Row) => ReactNode;
  align?: TableAlign;
  /**
   * SOCLE de la colonne (px), rembourrage compris : largeur PRÉFÉRÉE du `<col>` ET plancher de la
   * colonne. Sous `table-layout:auto`, le navigateur part de là et ÉLARGIT pour loger le contenu — il
   * ne descend jamais en dessous. Sert aussi au plancher de défilement.
   * ⚠️ CE N'EST PLUS UNE PISTE IMPOSÉE (changement de contrat, 2026-08-16) : sous `fixed`, une cellule
   * trop chargée n'avait aucun recours — elle débordait sur sa voisine, puis, le débordement coupé,
   * elle était amputée. Une largeur est désormais un point de départ, pas une sentence.
   * ⚠️ Une colonne SANS `width` est celle qui ABSORBE le reliquat — la PREMIÈRE déclarée sans socle,
   * une seule par tableau ; son plancher est `TABLE.width.growMin`.
   * ⚠️ Une seule largeur pour les deux : les cellules sont en `box-sizing:border-box` côté charte,
   * donc ce nombre est celui que le navigateur applique. Ajouter le rembourrage au plancher le
   * comptait deux fois (sur huit colonnes : 256 px de défilement horizontal fictif).
   */
  width?: number;
  /** Plancher RÉEL, quand il est inférieur au socle (« 188 souhaités, 168 incompressibles »). Défaut : `width`. */
  minWidth?: number;
  /** Plafond de croissance. ⚠️ C'est LUI qui rend l'ellipse atteignable : sans plafond, rien ne tronque. */
  maxWidth?: number;
  /**
   * Colonne FIGÉE : plafond = socle, elle ne bouge plus d'une page à l'autre.
   * ⚠️ Réservé à ce qui se COMPARE verticalement — des montants dont la colonne changerait de largeur
   * au fil des pages se lisent mal. Pour tout le reste, laisser la colonne respirer.
   */
  rigid?: boolean;
  /** Colonne CHIFFRÉE : chiffres à chasse fixe + alignée à droite, d'office (quantités et compteurs compris). */
  numeric?: boolean;
  /** Identifiant technique : rendu en police mono — « une information n'a pas l'apparence d'un champ ». */
  mono?: boolean;
  /** Triable. ⚠️ N'a d'effet qu'avec `onSortChange` : le TRI lui-même est fait par l'appelant (serveur). */
  sortable?: boolean;
  /** Sens du PREMIER clic. Défaut déduit de `numeric` (un nombre se lit d'abord du plus grand). */
  defaultSortDir?: SortDir;
  /**
   * Colonne qui IDENTIFIE la ligne : non masquable (cadenas dans le popover).
   * ⚠️ `locked` ne dit RIEN de sa place : une colonne verrouillée en visibilité reste déplaçable.
   */
  locked?: boolean;
  /** Défaut : `!locked`. Une colonne non masquable n'offre aucune bascule dans le popover. */
  hideable?: boolean;
  /**
   * Épinglage : reste en tête / en queue quel que soit l'ordre choisi, donc NON DÉPLAÇABLE.
   * ⚠️ SÉPARÉ de `locked` (arbitrage utilisateur 2026-08-16), et c'est ce qui réconcilie deux textes
   * contradictoires : la charte dit « verrouillée = ni masquable NI déplaçable », le CADRAGE dit que
   * Famille/Prix TTC/Stock sont verrouillées en visibilité mais SE DÉPLACENT. Un seul cadenas
   * recouvrait deux intentions, d'où deux comportements incompatibles selon le document lu.
   */
  pinned?: 'start' | 'end';
  /**
   * Mode carte (< 768 px) : `false` = cellule masquée (vignette…), sinon intitulé devant la valeur.
   * ⚠️ AUCUN PLAFOND DE CHAMPS (décision 2026-08-16) : le dessin veut cinq champs conservés, mais
   * masquer le reste sans affordance « voir tout » ferait montrer à la carte MOINS que le tableau
   * sans le dire. La priorité de carte a donc été retirée du contrat plutôt que laissée en promesse.
   */
  card?: false | { label?: string };
  /** Infobulle d'en-tête (unité, méthode de calcul). */
  headerTitle?: string;
}

// ── TRI (contrôlé) ─────────────────────────────────────────────────────────────────────────────
export type SortDir = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDir }

// ── PAGINATION (contrôlée) ─────────────────────────────────────────────────────────────────────
export interface PageState {
  /** Index 0-based de la page affichée. */
  index: number;
  /** Lignes par page. */
  size: number;
  /**
   * Total de lignes du jeu FILTRÉ, tel que le serveur le connaît.
   * ⚠️ `'unknown'` est une valeur LÉGITIME : quand le serveur ne compte pas, le seul total disponible
   * côté client est `rows.length` — le nombre de lignes de la PAGE. L'afficher comme un total serait un
   * KPI fabriqué (§23.0). Rendre l'ignorance représentable est la seule façon d'empêcher qu'on la
   * comble par un chiffre faux.
   */
  total: number | 'unknown';
}

// ── SÉLECTION ──────────────────────────────────────────────────────────────────────────────────
/**
 * Deux natures, jamais mélangées : des lignes DÉSIGNÉES, ou le jeu filtré désigné par son PRÉDICAT.
 * ⚠️ Avec une pagination serveur, « Tout sélectionner (N) » ne PEUT pas être une liste d'identifiants :
 * ceux des pages non chargées n'existent pas côté client. C'est un critère, et l'app l'envoie tel quel.
 */
export type Selection =
  | { kind: 'ids'; ids: ReadonlySet<string> }
  | { kind: 'criteria'; total: number; excluded: ReadonlySet<string> };

export interface SelectionConfig<Row> {
  value: Selection;
  onChange: (next: Selection) => void;
  /**
   * Une ligne peut refuser d'être sélectionnée, et DIT pourquoi (l'infobulle porte la raison).
   * ⚠️ POSER CE PRÉDICAT RETIRE « Tout sélectionner (N) » — cf. `canSelectAllMatching`. Un critère ne
   * transporte pas un prédicat écrit en JavaScript côté client.
   */
  selectable?: (row: Row) => true | string;
  /**
   * Le serveur sait-il agir sur un CRITÈRE (et non sur une liste d'ids) ?
   * ⚠️ L'offre a TROIS conditions d'APTITUDE (`canSelectAllMatching`) — ce drapeau, un total connu,
   * aucun prédicat `selectable` — et TROIS de SITUATION (`shouldOfferSelectAllMatching`) : encore en
   * `ids`, page entièrement cochée, jeu filtré plus grand que la page. Poser ce drapeau ne suffit donc
   * pas à faire apparaître le bouton, et c'est délibéré : offrir une portée que le back ne sait pas
   * exécuter serait une option offerte non implémentée (§23.0).
   * ⚠️ Compter « quatre » en agrégeant la situation en un seul point, comme le faisait ce commentaire,
   * donnait un troisième décompte pour la même règle — le SPEC en annonçait un quatrième. Une décision
   * qui se raconte de trois façons finit par se coder de trois façons : 3 + 3, partout.
   */
  allowSelectAllMatching?: boolean;
}

// ── ACTIONS DE MASSE ───────────────────────────────────────────────────────────────────────────
export interface BulkAction {
  id: string;
  label: string;
  tone?: 'neutral' | 'danger';
  /**
   * ⚠️ PROP OBLIGATOIRE, sans valeur par défaut : c'est la barrière de TYPE qui interdit de reproduire
   * les quatre boutons sans gestionnaire de la maquette (Activer/désactiver, Changer de famille,
   * Appliquer un coefficient, Exporter). Une action sans exécution ne peut pas être déclarée.
   * ⚠️ Une promesse REJETÉE est rendue visible (alerte bloquante, message réel) : le composant accepte
   * une promesse, il ne la jette pas.
   */
  run: (selection: Selection) => void | Promise<void>;
  /** Raison du refus (chaîne) ou `false`. La raison s'affiche en infobulle — jamais un bouton gris muet. */
  disabled?: (selection: Selection) => string | false;
  /**
   * Action de masse ou irréversible → confirmation obligatoire (un toast annulable ne suffit pas).
   * ⚠️ Posée, elle EST demandée : le canal partagé `ui-web/confirm` pose la question avant `run()`.
   * Sans hôte de dialogue monté, ce canal répond NON — une action de masse ne part jamais par défaut.
   */
  confirm?: { title: string; message: string; confirmLabel: string };
}

// ── ÉTAT DE CHARGEMENT ─────────────────────────────────────────────────────────────────────────
/**
 * ⚠️ CINQ ÉTATS EXCLUSIFS, ET AUCUN SQUELETTE. Un squelette dessine des lignes qui n'existent pas et
 * prétend en connaître le nombre : c'est une donnée inventée rendue comme réelle, doublée d'une
 * progression au chronomètre. Le chargement se DIT, il ne se mime pas (§23.0).
 */
export type TableStatus =
  /** Rien à charger encore (aucune requête partie) — DISTINCT d'un chargement, cf. `bodyKind`. */
  | { phase: 'idle' }
  /** Premier chargement : corps vide + libellé. Aucune ligne fantôme. */
  | { phase: 'loading' }
  /** Rechargement : les lignes PRÉCÉDENTES restent, marquées périmées, actions de masse gelées. */
  | { phase: 'reloading' }
  | { phase: 'ready' }
  /**
   * Échec. `message` = le message RÉEL de l'exception, jamais une phrase rassurante réécrite.
   * ⚠️ AUCUNE ligne n'est rendue : afficher les anciennes sous une erreur laisserait croire qu'elles
   * sont à jour — c'est un échec avalé, la pire des six façons de mentir parce qu'elle est invisible.
   */
  | { phase: 'error'; message: string; detail?: string; onRetry?: () => void };

// ── ÉTATS VIDES (deux, distincts) ──────────────────────────────────────────────────────────────
export interface TableEmptyStates {
  /** Aucune donnée du tout : dit ce qui manque et ce qui le remplira. */
  empty: { icon: IconName; title: string; note: ReactNode; action?: ReactNode };
  /**
   * Des filtres sont actifs et ne ramènent rien. OBLIGATOIRE dès qu'un filtre peut être posé :
   * « un état vide qui ne dit pas comment en sortir est un cul-de-sac ». Et confondre les deux
   * fait dire « aucun article » à un catalogue de 30 000 références filtré sur une famille vide.
   */
  noMatch?: { title: string; note: ReactNode; clearLabel?: string };
}

// ── CHIPS DE FILTRE ────────────────────────────────────────────────────────────────────────────
export interface FilterChip { id: string; label: string; onRemove: () => void }

// ── UNITÉ ÉNUMÉRÉE ─────────────────────────────────────────────────────────────────────────────
/**
 * Ce que le tableau compte, au singulier — avec son GENRE.
 * ⚠️ Le genre n'est pas un raffinement : le compteur de sélection accorde un participe. Figé au
 * masculin, il écrivait « 3 pièces sélectionnés » — et « pièce » est l'unité même que la doc citait
 * en exemple. Une chaîne nue reste acceptée et vaut masculin, forme majoritaire du dépôt.
 * ⚠️ `one` S'AFFICHE — dans le libellé accessible « Sélectionner {name} » — donc il arrive TRADUIT. Et il n'y a
 *    pas de forme plurielle à fournir : un pluriel passé ici serait un pluriel fait à la main, et ce qui se compte
 *    passe par une phrase comptée du catalogue.
 */
export interface TableUnit { one: string; gender?: 'f' | 'm' }

// ── EXPORT ─────────────────────────────────────────────────────────────────────────────────────
/**
 * LA DESCRIPTION DE LA VUE — ce que l'écran montre, jamais ce qu'il contient.
 *
 * ⚠️ AUCUNE LIGNE, ET C'EST LE POINT (arbitrage utilisateur 2026-08-16). L'export se fait CÔTÉ
 * SERVEUR, avec les filtres, les colonnes visibles et le tri de la vue, sur le jeu filtré ENTIER : le
 * composant remonte donc une INTENTION (quelles colonnes, dans quel ordre, trié comment) que l'app
 * complète de ses filtres et envoie au serveur. Une passe précédente faisait porter à cette structure
 * les valeurs brutes de la PAGE (`exportRows`, alimentées par un `ColumnDef.exportValue`) : un bouton
 * intitulé « Export Excel » produisait alors 25 lignes sur 1 248, sans que rien ne le dise. Les deux
 * ont été retirés plutôt que documentés — ce qui n'est pas branché s'enlève (§23.0).
 */
export interface TableView<Row> {
  /** Colonnes visibles, DANS l'ordre affiché (verrouillées et épinglées comprises). */
  columns: ColumnDef<Row>[];
  sort: SortState | null;
  page: PageState;
  total: number | 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// LOGIQUE PURE — extraite du rendu, et testée SANS DOM.
//
// ⚠️ Ce n'est pas un contournement de l'absence de jsdom dans ce paquet : ce sont les décisions du
// tableau (quel tri au prochain clic, quelles colonnes visibles, quel plancher de largeur, que dit le
// pied) qui doivent être tenues par des tests. Un test de rendu vérifierait qu'un `<button>` existe ;
// ceux-ci vérifient ce que le composant DÉCIDE — et ils survivent à une refonte du balisage.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/** Sens du PREMIER clic sur une colonne : ascendant pour du texte, descendant pour un nombre/une date. */
export function firstSortDir<Row>(col: ColumnDef<Row>): SortDir {
  return col.defaultSortDir ?? (col.numeric ? 'desc' : 'asc');
}

/** Tri voulu après un clic sur `col` : inversion si elle est déjà active, sens naturel sinon. */
export function nextSort<Row>(col: ColumnDef<Row>, current: SortState | null | undefined): SortState {
  if (current && current.key === col.key) return { key: col.key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key: col.key, dir: firstSortDir(col) };
}

/**
 * Une colonne est-elle TRIABLE À L'ÉCRAN ? Elle doit le déclarer ET le tableau doit savoir quoi faire
 * du clic.
 * ⚠️ Les deux conditions vont ensemble PARTOUT (classe, `aria-sort`, rendu du bouton) : `sortable`
 * seul produisait « un en-tête qui a l'air triable et ne trie pas » — annoncé « colonne triable, non
 * triée » au lecteur d'écran, et privé de son rembourrage puisque celui-ci vit sur le bouton absent.
 */
export function isColumnSortable<Row>(col: ColumnDef<Row>, hasSortHandler: boolean): boolean {
  return Boolean(col.sortable && hasSortHandler);
}

/** Valeur `aria-sort` d'un en-tête. Une colonne triable non active vaut `none`, pas rien. */
export function ariaSortOf<Row>(
  col: ColumnDef<Row>,
  sort: SortState | null | undefined,
  hasSortHandler = true,
): 'ascending' | 'descending' | 'none' | undefined {
  if (!isColumnSortable(col, hasSortHandler)) return undefined;
  if (!sort || sort.key !== col.key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

/**
 * Colonnes visibles, dans l'ordre affiché.
 * ⚠️ `reconcileColumnLayout` est appliquée ICI, donc à CHAQUE rendu : un réglage enregistré l'an
 * dernier ne peut pas faire disparaître en silence une colonne ajoutée depuis.
 * ⚠️ Les épinglées sortent de l'ordre mémorisé et reprennent leur bord : une colonne d'identité qui
 * migrerait au milieu du tableau rendrait la lecture impossible sans rien annoncer.
 * ⚠️ `sortByPinnedRank` et `isColumnHideable` viennent de `ui-tokens` et NE SONT PAS réécrites ici :
 * le popover Colonnes prend exactement les deux mêmes décisions, et une recette recopiée des deux
 * côtés a déjà divergé une fois — le rendu classait les épinglées avec sa propre formule pendant que
 * le panneau appelait la fonction partagée.
 */
export function visibleColumns<Row>(columns: ColumnDef<Row>[], layout?: ColumnLayout | null): ColumnDef<Row>[] {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const declared = columns.map((c) => c.key);
  const eff = reconcileColumnLayout(declared, layout ?? null);
  const hidden = new Set(eff.hidden);
  const ordered: ColumnDef<Row>[] = [];
  for (const key of eff.order) {
    const col = byKey.get(key);
    if (!col) continue;
    if (hidden.has(key) && isColumnHideable(col)) continue; // une non-masquable résiste à un réglage qui la masque
    ordered.push(col);
  }
  return sortByPinnedRank(ordered, (c) => c.pinned);
}

/**
 * Plancher de défilement du tableau, en pixels — 0 quand aucune colonne ne déclare de largeur.
 * ⚠️ Jamais un nombre en dur : le `min-width:1262px` de la maquette ne correspond ni à la somme de ses
 * pistes (~1386 px) ni à une configuration nommée. Recopié, il ROGNE au lieu de protéger.
 */
export function tableFloorWidth<Row>(cols: ColumnDef<Row>[], gutters: number): number {
  if (cols.every((c) => c.width === undefined)) return 0;
  // ⚠️ Le plancher compte le PLANCHER de chaque colonne, pas son socle : une colonne peut souhaiter
  // 188 px et n'en exiger que 168. Compter les socles imposerait un défilement horizontal plus tôt
  // que nécessaire.
  return tableMinWidth(cols.map((c) => c.minWidth ?? c.width ?? TABLE.width.growMin), gutters);
}

/**
 * Bornes de largeur d'une colonne, en VARIABLES — la charte pose les déclarations.
 * ⚠️ ÉMISES SUR LE `<th>` ET SUR LE `<td>` : en largeur adaptative, la largeur d'une colonne est la
 * résultante de TOUTES ses cellules ; n'en borner qu'une ne borne rien.
 * ⚠️ Pas de style en ligne pour le min/max : un `min-width` en ligne survivrait au repli en cartes
 * (une media-query ne bat pas un attribut `style`) et ferait défiler une carte de 360 px.
 */
export function columnSizeVars<Row>(c: ColumnDef<Row>): CSSProperties {
  const min = c.minWidth ?? c.width ?? TABLE.width.growMin;
  const max = c.rigid ? (c.maxWidth ?? c.width) : c.maxWidth;
  return { '--dt-cw-min': `${min}px`, ...(max ? { '--dt-cw-max': `${max}px` } : null) } as CSSProperties;
}

/** Variables de densité posées sur la carte. La densité PILOTE vraiment la hauteur de rangée. */
export function densityVars(density: TableDensity = TABLE_DEFAULTS.density, rowHeight?: number): Record<string, string> {
  const d = TABLE_DENSITY[density];
  return {
    // ⚠️ MISE À L’ÉCHELLE, jamais posée en dur : une vue peut déclarer son relevé (la rangée Articles
    // porte une vignette de 34 px et respire mal à 44), et le cran de densité continue de la piloter.
    '--dt-row-h': `${rowHeightFor(density, rowHeight)}px`,
    '--dt-row-fs': `${d.rowFontSize}px`,
    '--dt-head-pv': `${d.headerPadV}px`,
  };
}

/** Une ligne est-elle dans la sélection courante ? Les deux natures se lisent de la même façon ici. */
export function isRowSelected(sel: Selection, id: string): boolean {
  return sel.kind === 'ids' ? sel.ids.has(id) : !sel.excluded.has(id);
}

/** Bascule d'UNE ligne. En mode critère, décocher AJOUTE une exclusion — on ne perd pas le prédicat. */
export function toggleRowSelection(sel: Selection, id: string): Selection {
  if (sel.kind === 'ids') {
    const ids = new Set(sel.ids);
    if (!ids.delete(id)) ids.add(id);
    return { kind: 'ids', ids };
  }
  const excluded = new Set(sel.excluded);
  if (!excluded.delete(id)) excluded.add(id);
  return { kind: 'criteria', total: sel.total, excluded };
}

/** Nombre de lignes réellement sélectionnées — un critère moins ses exclusions reste un compte exact. */
export function selectionSize(sel: Selection): number {
  return sel.kind === 'ids' ? sel.ids.size : Math.max(0, sel.total - sel.excluded.size);
}

/**
 * Portée « toutes les lignes du filtre » : un CRITÈRE, jamais une liste d'identifiants (ceux des pages
 * non chargées n'existent pas côté client).
 */
export function selectAllMatching(total: number): Selection {
  return { kind: 'criteria', total, excluded: new Set() };
}

/**
 * La sélection VIDE.
 * ⚠️ Toujours de nature `ids`, et ce n'est pas une réécriture arbitraire : un critère PORTE un total,
 * il n'a pas de forme vide. « Plus rien de sélectionné » n'a qu'une écriture — en inventer une seconde
 * (un critère dont tout serait exclu) obligerait chaque appelant à traiter deux fois le même cas.
 */
export function clearSelection(): Selection {
  return { kind: 'ids', ids: new Set() };
}

/**
 * « Tout sélectionner (N) » est-il proposable ? TROIS conditions d'APTITUDE.
 * ⚠️ (1) l'app déclare que le serveur sait agir par critère ; (2) le total est connu — sans lui le
 * bouton annoncerait un nombre qu'on ne possède pas ; (3) AUCUN prédicat `selectable` n'est posé.
 * ⚠️ La troisième a été ajoutée le 2026-08-16, après un trou réel : un critère porte un total et un
 * jeu d'exclusions, PAS un prédicat écrit en JavaScript côté client. « Tout sélectionner (1 248) »
 * cochait donc aussi les lignes que l'app refuse explicitement de laisser sélectionner — elles
 * apparaissaient cochées, leur case était inerte, et elles étaient comptées dans N puis envoyées à
 * une action de masse. On ne peut pas brancher cette portée quand un refus par ligne existe : on la
 * retire (§23.0). Une app qui veut l'offrir exprime son refus dans le FILTRE serveur, où il vit.
 */
export function canSelectAllMatching<Row>(cfg: SelectionConfig<Row> | undefined, page: PageState | undefined): boolean {
  if (!cfg?.allowSelectAllMatching || cfg.selectable) return false;
  return typeof page?.total === 'number';
}

/**
 * Le composant REND-il l'offre « Tout sélectionner (N) » ?
 * ⚠️ Au-delà de l'aptitude (`canSelectAllMatching`), trois conditions de SITUATION : on est encore en
 * sélection par identifiants, la page entière est cochée (c'est le moment où la question se pose), et
 * le jeu filtré DÉPASSE la page — proposer d'étendre une portée à ce qui y est déjà serait un bouton
 * qui fait semblant.
 */
export function shouldOfferSelectAllMatching<Row>(
  cfg: SelectionConfig<Row> | undefined,
  page: PageState | undefined,
  pageRowCount: number,
  pageAllSelected: boolean,
): boolean {
  if (!canSelectAllMatching(cfg, page) || !pageAllSelected) return false;
  if (cfg?.value.kind !== 'ids') return false;
  return typeof page?.total === 'number' && page.total > pageRowCount;
}

/**
 * La barre de sélection est-elle rendue ?
 * ⚠️ ELLE NE DÉPEND PAS DES ACTIONS DE MASSE (correctif 2026-08-16). Elle en dépendait, et cela
 * fabriquait une quatrième condition que rien ne documentait : une table à sélection SANS
 * `bulkActions` n'affichait ni son compteur, ni « Annuler la sélection », ni « Tout sélectionner (N) »
 * — une app qui posait `allowSelectAllMatching` obtenait alors un écran identique à celle qui ne le
 * posait pas. La barre dit ce qui est sélectionné et comment y renoncer ; les actions, quand il y en
 * a, s'y ajoutent.
 */
export function shouldShowBulkBar<Row>(cfg: SelectionConfig<Row> | undefined, selectedCount: number): boolean {
  return Boolean(cfg) && selectedCount > 0;
}

/** Pluriel simple, pour les compteurs (« 1 article » / « 3 articles »). */
export function plural(n: number, singular: string, pluralForm?: string): string {
  return n > 1 ? (pluralForm ?? `${singular}s`) : singular;
}

/**
 * Compteur de la barre de masse — « 7 sélectionnés ».
 *
 * ⚠️ SANS LE NOM DE L'OBJET, exactement comme la maquette (décision utilisateur 2026-08-16). Ce compteur
 * a porté « 7 modèles sélectionnés » : plus informatif sur une barre partagée par sept tableaux, mais
 * ce n'est pas ce que la maquette écrit, et c'est elle qui fait foi.
 * ⚠️ `unit` reste consommée pour le GENRE : sans lui on écrirait « 3 sélectionnés » sur des pièces.
 * C'est la seule chose que l'unité apporte encore ici — d'où le paramètre conservé.
 */
export function selectionLabel(count: number, unit: string | TableUnit): string {
  const u: TableUnit = typeof unit === 'string' ? { one: unit } : unit;
  // ⚠️ DEUX CLÉS, PARCE QUE LE GENRE NE SE CALCULE PAS. Le français accorde le participe avec l'unité
  //    (« 3 lignes sélectionnées » / « 3 articles sélectionnés »), et aucun mécanisme de pluriel ne
  //    porte le genre. Une seule clé à trou — « {n} sélectionné{e} » — livrerait au traducteur un
  //    morceau de terminaison, ce qui n'a de sens dans aucune autre langue.
  return u.gender === 'f' ? t('{n} sélectionnée', { n: count }) : t('{n} sélectionné', { n: count });
}

/**
 * Ce que le pied de pagination AFFICHE.
 * ⚠️ Quand le total est inconnu, il rend « Page n » et RIEN d'autre : pas de « sur N », pas de plage
 * de lignes. `rows.length` est le compte d'une page, jamais un total.
 * ⚠️ Une page VIDE rend « Page n » elle aussi : la formule de plage produisait « 51–50 sur 132 » sur
 * une page hors bornes, c'est-à-dire un intervalle inversé donné pour une position réelle.
 */
export function pageLabel(page: PageState, rowsOnPage: number): string {
  if (page.total === 0) return '0';
  if (page.total === 'unknown' || rowsOnPage === 0) return t('Page {v}', { v: page.index + 1 });
  const from = page.index * page.size + 1;
  return t('{v}–{v2} sur {total}', { v: from, v2: from + rowsOnPage - 1, total: page.total });
}

/** Y a-t-il une page suivante ? Inconnu ⇒ on se fie au remplissage de la page courante. */
export function hasNextPage(page: PageState, rowsOnPage: number): boolean {
  if (page.total === 'unknown') return rowsOnPage >= page.size;
  return (page.index + 1) * page.size < page.total;
}

/**
 * Une flèche de pagination est-elle réellement actionnable ?
 *
 * ⚠️ LE GESTIONNAIRE COMPTE AUTANT QUE LA BORNE. `page` et `onPageChange` sont deux props
 * indépendantes : une table peut donc annoncer sa pagination sans savoir en changer. Les flèches
 * étaient alors rendues actives et avalaient le clic — « un bouton qui fait semblant » (§23.0).
 * C'est le défaut déjà corrigé pour le tri par `isColumnSortable` ; la pagination en était la dernière
 * exception. Extrait en fonction PURE pour que la garde soit vérifiable sans DOM.
 */
export function canGoToPage(
  page: PageState,
  rowsOnPage: number,
  hasPageHandler: boolean,
  dir: 'prev' | 'next',
): boolean {
  if (!hasPageHandler) return false;
  return dir === 'prev' ? page.index > 0 : hasNextPage(page, rowsOnPage);
}

/**
 * Quel corps rendre. Une seule fonction, pour que les cas ne puissent pas se chevaucher — c'est le
 * chevauchement (des lignes SOUS une erreur, un squelette PENDANT un chargement) qui fait mentir l'écran.
 * ⚠️ `idle` a son PROPRE corps : le replier sur `loading` faisait écrire « Chargement… » alors
 * qu'aucune requête n'était partie — un libellé qui nomme une opération inexistante (§23.0), et un
 * écran bloqué en attente d'une dépendance y restait indéfiniment sans jamais révéler qu'il n'avait
 * rien demandé.
 */
export type TableBody = 'rows' | 'loading' | 'idle' | 'error' | 'empty' | 'noMatch';
export function bodyKind(status: TableStatus, rowCount: number, hasActiveFilters: boolean): TableBody {
  if (status.phase === 'error') return 'error';
  if (status.phase === 'loading') return 'loading';
  if (rowCount > 0) return 'rows';
  if (status.phase === 'idle') return 'idle';
  return hasActiveFilters ? 'noMatch' : 'empty';
}

/**
 * Le pied de pagination est-il rendu ?
 * ⚠️ Il SURVIT AUX DEUX VIDES dès qu'on n'est pas en première page : sinon une page hors bornes
 * (page 3, puis un filtre qui n'en laisse que 2) devenait un cul-de-sac — l'écran affirmait « aucune
 * ligne » alors qu'il y en a, sur une autre page, et retirait le seul contrôle permettant d'y aller.
 * ⚠️ MAIS JAMAIS SOUS `error`, `loading` NI `idle` (correctif 2026-08-16). La survie avait été
 * accordée à tout corps sans lignes : un écran en page 3 qui échoue affichait « Échec du chargement »
 * au-dessus d'un pied écrivant « 51–75 sur 132 » et « Trié par Marge (décroissant) » — une plage de
 * lignes affirmée alors que le corps refuse d'en rendre une seule, calculée qui plus est sur des
 * `rows` que rien ne vide. C'est exactement le §23.0 que `TableStatus` défend pour le corps, déplacé
 * dans le pied : un correctif anti-cul-de-sac qui fabriquait un mensonge.
 */
export function shouldShowFoot(page: PageState | undefined, body: TableBody): boolean {
  if (!page) return false;
  if (body === 'rows') return true;
  return (body === 'empty' || body === 'noMatch') && page.index > 0;
}

/**
 * Ce que le pied dit du TRI, ou `null` s'il n'y en a pas.
 * ⚠️ Il nomme la colonne masquée pour ce qu'elle est : chercher le libellé dans les colonnes DÉCLARÉES
 * faisait affirmer « Trié par Marge » sur une donnée que l'utilisateur ne voit plus et ne peut plus
 * atteindre pour changer d'ordre. Le tri reste vrai — c'est son support qui a disparu, et ça se dit.
 */
export function sortNoticeLabel<Row>(
  columns: ColumnDef<Row>[],
  visible: ColumnDef<Row>[],
  sort: SortState | null | undefined,
): string | null {
  if (!sort) return null;
  const label = columns.find((c) => c.key === sort.key)?.label ?? sort.key;
  const way = sort.dir === 'asc' ? t('croissant') : t('décroissant');
  const shown = visible.some((c) => c.key === sort.key);
  return shown
    ? t('Trié par {label} ({way})', { label, way })
    : t('Trié par {label} ({way}) — colonne masquée', { label, way });
}

/**
 * Options du sélecteur « par page ».
 * ⚠️ La taille RÉELLE y est injectée quand elle sort de la gamme : le dépôt pagine déjà par 14 et par
 * 60, et un `<select>` sans option correspondante s'affiche sur une AUTRE valeur — le contrôle
 * annonçait alors une taille de page qui n'était pas celle du tableau.
 */
export function pageSizeOptions(current: number): number[] {
  return [...new Set<number>([...TABLE_PAGE_SIZES, current])].sort((a, b) => a - b);
}

/**
 * La vue à EXPORTER : colonnes VISIBLES dans l'ordre AFFICHÉ, tri courant, page courante.
 * ⚠️ Elle ne lit AUCUNE ligne — l'export est produit par le serveur sur le jeu filtré entier. Lui
 * faire porter les lignes de la page rendrait un export partiel indiscernable d'un export complet.
 */
export function buildTableView<Row>(
  columns: ColumnDef<Row>[],
  layout: ColumnLayout | undefined,
  sort: SortState | null,
  page: PageState,
): TableView<Row> {
  return { columns: visibleColumns(columns, layout), sort, page, total: page.total };
}

/**
 * Attributs de CARTE d'une cellule (< 768 px) — UNE recette, pour le corps ET pour la ligne de total.
 *
 * ⚠️ `data-label` est posé INCONDITIONNELLEMENT : c'est lui qui porte l'intitulé en mode carte
 * (`::before{content:attr(data-label)}`). La maquette a produit deux fois des cartes affichant des
 * valeurs nues parce que le libellé n'était posé que quand on y pensait.
 * ⚠️ Recette PARTAGÉE parce que le total avait été oublié : une colonne `card:false` disparaissait du
 * corps mais son TOTAL restait affiché, intitulé compris. Un chiffre isolé, sans sa colonne, est une
 * donnée que l'écran ne sait plus qualifier.
 */
export function cardCellAttrs<Row>(col: ColumnDef<Row>): { 'data-label': string; 'data-card'?: 'off' } {
  if (col.card === false) return { 'data-label': col.label, 'data-card': 'off' };
  return { 'data-label': col.card?.label ?? col.label };
}

/** Le message RÉEL de l'échec — jamais une phrase rassurante réécrite (§23.0). */
function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ── PROPS ──────────────────────────────────────────────────────────────────────────────────────
export interface DataTableProps<Row> {
  /** Identité de la table : clé de persistance des colonnes, `id` ARIA, `data-table` de test. */
  tableId: string;
  /** Ce que le tableau énumère, au singulier (« article », `{ one: 'pièce', gender: 'f' }`). */
  unit: string | TableUnit;

  columns: ColumnDef<Row>[];
  /** UNIQUEMENT les lignes de la page courante. Le composant n'en découpe aucune. */
  rows: Row[];
  /** Identité stable d'une ligne : sélection, dépliage, mise en avant. */
  getRowId: (row: Row) => string;

  status: TableStatus;
  emptyStates: TableEmptyStates;

  sort?: SortState | null;
  /** ⚠️ Rend le NOUVEL état voulu ; c'est à l'appelant de re-requêter ET de revenir en page 1. */
  onSortChange?: (next: SortState) => void;
  /**
   * Tri de repli, appliqué quand la colonne triée sort des colonnes visibles (règle du CADRAGE).
   * ⚠️ Sans lui le tableau ne peut que le DIRE au pied : il n'invente pas un ordre par défaut, ce
   * serait décider à la place de l'écran quel classement fait sens.
   */
  defaultSort?: SortState;

  page?: PageState;
  onPageChange?: (index: number) => void;
  /** Sélecteur « n par page ». Absent ⇒ non rendu (le back-office n'en a pas, la Console si). */
  onPageSizeChange?: (size: number) => void;

  // ── Colonnes (état exposé, JAMAIS persisté par le composant) ──
  columnLayout?: ColumnLayout;
  onColumnLayoutChange?: (next: ColumnLayout, reason: 'toggle' | 'move' | 'reset') => void;
  /**
   * Phrase de portée affichée au pied du popover (« mémorisés sur votre profil, tous vos postes »).
   * ⚠️ Rendue SEULEMENT si l'app la fournit. La maquette affiche cette phrase alors que rien n'est
   * branché : une promesse de mémorisation non tenue fait croire à un bug plutôt qu'à une absence.
   */
  columnPersistenceNote?: string;
  /** Réordonnancement possible ? Défaut `false` (5 tableaux sur 7 n'ont que la visibilité). */
  reorderableColumns?: boolean;

  selection?: SelectionConfig<Row>;
  bulkActions?: BulkAction[];

  /** Clic n'importe où sur la ligne. Précédence imposée : case → chevron → ligne. */
  onRowActivate?: (row: Row) => void;
  /** Teinte SÉMANTIQUE de ligne, par rôle (jamais une couleur). */
  rowTone?: (row: Row) => 'pending' | 'muted' | 'danger' | undefined;
  /** Dépliage en place (matrice taille × couleur, détail de pièce). */
  expandable?: { render: (row: Row) => ReactNode; expandedIds: ReadonlySet<string>; onToggle: (id: string) => void };
  /**
   * Hauteur de rangée RELEVÉE pour cette vue, quand elle diffère du cran par défaut.
   * ⚠️ Elle est mise à l’ÉCHELLE par la densité, pas substituée : le réglage de compte continue de
   * piloter. Absente = le cran s’applique tel quel.
   */
  rowHeight?: number;
  /** Ligne mise en avant. ⚠️ Le SAUT DE PAGE appartient à l'app : elle seule sait où vit la ligne. */
  focusRowId?: string;
  /** Ligne de total, sous le corps. Elle ferme une arithmétique et DIT ce qu'elle exclut (`note`). */
  totalRow?: { cells: Partial<Record<string, ReactNode>>; note?: ReactNode };

  /** Filtres et recherche : composés par l'app, posés dans la barre du tableau. */
  toolbar?: ReactNode;
  /**
   * Les filtres APPLIQUÉS, en puces révocables.
   * ⚠️ RENDUES DÈS QU'ELLES SONT LÀ, sans autre condition — et c'est ce qui rend la persistance des
   * filtres acceptable (arbitrage utilisateur 2026-08-16 : persistés par utilisateur × par écran).
   * Le conflit ne portait pas sur la persistance mais sur la restauration SILENCIEUSE : rouvrir un
   * écran fiscal avec un filtre invisible fait croire qu'on voit tout. Le composant ne persiste rien
   * (l'app le fait, clé `tableFiltersKey`) ; il garantit seulement qu'un filtre restauré SE VOIT.
   */
  filterChips?: FilterChip[];
  onClearFilters?: () => void;
  /**
   * Des filtres sont-ils POSÉS ? Défaut : la présence de chips.
   * ⚠️ À déclarer par les écrans qui filtrent SANS chips. Jamais déduit de `onClearFilters` : ce
   * gestionnaire est fourni en permanence par une barre d'outils, et l'en déduire rendait l'état vide
   * honnête inatteignable — un catalogue neuf annonçait « les filtres actifs ne ramènent aucune
   * ligne » et proposait d'effacer des filtres fantômes.
   */
  filtersActive?: boolean;
  /**
   * Intention d'export, remontée à l'app. Rendue SEULEMENT si fournie ; libellé imposé « Export Excel ».
   * ⚠️ LE COMPOSANT NE FABRIQUE AUCUN FICHIER, et ne lit même pas les lignes : le MÉCANISME est côté
   * SERVEUR (arbitrage utilisateur 2026-08-16), avec les filtres, les colonnes visibles et le tri de
   * la vue, sur le jeu filtré ENTIER. L'app reçoit la description de la vue et y joint ses filtres.
   * ⚠️ Un rejet est RENDU VISIBLE : sans cela un export raté laissait l'écran identique à un réussi.
   */
  onExport?: (view: TableView<Row>) => void | Promise<void>;

  density?: TableDensity;
  /** En-tête collant. Défaut `true` ; impose un conteneur de défilement borné (cf. `maxHeight`). */
  stickyHeader?: boolean;
  /** Défaut `TABLE.scroll.maxHeight`. ⚠️ `'none'` DÉSACTIVE l'en-tête collant : sans bornage il décroche. */
  maxHeight?: string | 'none';
  className?: string;
  style?: CSSProperties;
}

/** Libellé UNIQUE de l'export — jamais « Exporter », « CSV » ni « Télécharger » (charte §3d). */
export const EXPORT_LABEL = key('Export Excel');

/**
 * LES ATTRIBUTS D'UNE CELLULE DE GOUTTIÈRE — les mêmes pour l'en-tête, la ligne et le total.
 *
 * ⚠️ EXTRAIT APRÈS UN DÉFAUT RÉEL (2026-08-16) : le `<th>` de la colonne de sélection avait été écrit
 * sans `data-gutter`, alors que ses `<td>` le portaient. La règle qui met le rembourrage horizontal à
 * zéro ne s'appliquait donc QU'AU CORPS — la case « tout sélectionner » partait 16 px plus à droite
 * que les cases de ligne, et la colonne entière passait de 32 à 64 px (sous `table-layout:auto`, la
 * cellule la plus large impose la piste). Trois sites d'appel pour une même géométrie : c'est le site
 * d'appel qu'il fallait supprimer, pas ajouter un test qui surveille les trois.
 */
function gutterCell(kind: 'check' | 'expand', label: string) {
  return { className: 'dt-cell', 'data-gutter': kind, 'data-label': label } as const;
}
export function DataTable<Row>(props: DataTableProps<Row>) {
  const {
    tableId, unit, columns, rows, getRowId, status, emptyStates,
    sort = null, onSortChange, defaultSort, page, onPageChange, onPageSizeChange,
    columnLayout, onColumnLayoutChange, columnPersistenceNote, reorderableColumns,
    selection, bulkActions, onRowActivate, rowTone, expandable, focusRowId, totalRow, rowHeight,
    toolbar, filterChips, onClearFilters, filtersActive, onExport,
    density = TABLE_DEFAULTS.density, stickyHeader = true, maxHeight, className, style,
  } = props;

  const unitOne = typeof unit === 'string' ? unit : unit.one;
  const cols = useMemo(() => visibleColumns(columns, columnLayout), [columns, columnLayout]);
  // ⚠️ La PREMIÈRE gouttière porte le retrait de la carte (charte : «[data-gutter]:first-child«) — le
  // plancher de défilement doit compter la piste RENDUE, pas la piste déclarée, sinon il sous-estime
  // et le tableau comprime ses colonnes avant de défiler.
  const gutters = (selection ? TABLE.gutter.check + TABLE.row.padH : 0) + (expandable ? TABLE.gutter.expand + TABLE_EXPAND_LEAD : 0);
  const floor = useMemo(() => tableFloorWidth(cols, gutters), [cols, gutters]);
  /** La colonne qui absorbe la place restante : la PREMIÈRE sans socle déclaré. */
  const growKey = useMemo(() => cols.find((c) => c.width === undefined)?.key, [cols]);
  const hasFilters = filtersActive ?? (filterChips?.length ?? 0) > 0;
  const body = bodyKind(status, rows.length, hasFilters);
  const frozen = status.phase === 'reloading';
  const canSort = Boolean(onSortChange);

  // ⚠️ Le bornage et le collage vont ENSEMBLE : `'none'` renonce aux deux, jamais à un seul.
  const bounded = maxHeight !== 'none';
  const sticky = stickyHeader && bounded;

  // ⚠️ Masquer la colonne du tri en cours RÉINITIALISE le tri (règle du CADRAGE) — mais seulement si
  // l'app a dit sur quoi retomber. Sans repli déclaré, le pied se contente de nommer la situation.
  useEffect(() => {
    if (!sort || !onSortChange || !defaultSort) return;
    if (defaultSort.key === sort.key) return;
    if (cols.some((c) => c.key === sort.key)) return;
    onSortChange(defaultSort);
  }, [sort, cols, onSortChange, defaultSort]);

  const selected = useCallback(
    (row: Row) => (selection ? isRowSelected(selection.value, getRowId(row)) : false),
    [selection, getRowId],
  );

  const toggleRow = useCallback(
    (row: Row) => {
      if (!selection) return;
      if (selection.selectable && selection.selectable(row) !== true) return;
      selection.onChange(toggleRowSelection(selection.value, getRowId(row)));
    },
    [selection, getRowId],
  );

  // ⚠️ La case d'en-tête ne coche que les lignes de la PAGE : cocher au-delà de ce qui est chargé
  // reviendrait à désigner des identifiants qu'on n'a pas. La portée « toutes pages » est un CRITÈRE.
  const pageSelectables = rows.filter((r) => !selection?.selectable || selection.selectable(r) === true);
  const pageAllSelected = pageSelectables.length > 0 && pageSelectables.every(selected);
  const pageSomeSelected = pageSelectables.some(selected);

  const togglePage = () => {
    if (!selection) return;
    let next = selection.value;
    for (const row of pageSelectables) {
      const id = getRowId(row);
      const on = isRowSelected(next, id);
      if (on === pageAllSelected) next = toggleRowSelection(next, id);
    }
    selection.onChange(next);
  };

  const selCount = selection ? selectionSize(selection.value) : 0;
  const showBulk = shouldShowBulkBar(selection, selCount);
  const offerSelectAll = shouldOfferSelectAllMatching(selection, page, rows.length, pageAllSelected);
  const matchingTotal = page && typeof page.total === 'number' ? page.total : 0;

  /**
   * Dernier échec d'une action ou d'un export, quand le canal d'alerte n'a PAS d'hôte.
   * ⚠️ `confirm()`/`alert()` sont des canaux partagés qui exigent un `<DialogHost/>` monté ; sans lui,
   * `confirm()` répond non (sûr) mais `alert()` n'écrit qu'en console — l'échec redevient invisible à
   * l'écran, c'est-à-dire l'échec avalé. Ce filet n'est pas un second canal d'erreur : c'est le seul
   * qui reste quand l'app a oublié de monter le premier.
   */
  const [hostlessFailure, setHostlessFailure] = useState<string | null>(null);

  /** Montre un échec là où l'utilisateur regarde : par le canal de charte, ou par le filet à défaut. */
  const reportFailure = useCallback(async (title: string, message: string) => {
    if (hasDialogHost()) { setHostlessFailure(null); await alert({ title, message }); return; }
    setHostlessFailure(`${title} — ${message}`);
  }, []);

  /**
   * Exécute une action de masse : confirmation d'abord, échec RENDU VISIBLE ensuite.
   * ⚠️ Le rejet part en alerte bloquante avec le message réel — un `void run()` laissait l'écran
   * identique à celui d'un succès, ce qui se lit exactement comme une réussite (§23.0).
   */
  const runBulkAction = useCallback(async (action: BulkAction, sel: Selection) => {
    if (action.confirm) {
      // ⚠️ SANS HÔTE DE DIALOGUE, `confirm()` répond NON — c'est le bon défaut (une action de masse ne
      // part jamais par accident), mais rendu tel quel il produit le pire écran : on clique
      // « Supprimer (3) », rien ne bouge, et rien ne le dit. Le refus par sécurité doit donc passer par
      // le MÊME filet que les échecs, sinon la moitié des chemins reste muette (§23.0).
      if (!hasDialogHost()) {
        setHostlessFailure(
          t('{label} — confirmation impossible : aucun hôte de dialogue n’est monté dans cette application. L’action n’a PAS été exécutée.', { label: action.label }),
        );
        return;
      }
      const ok = await confirm({ ...action.confirm, tone: action.tone === 'danger' ? 'danger' : 'info' });
      if (!ok) return;
    }
    // ⚠️ L'échec précédent s'efface AU DÉPART de la nouvelle tentative, jamais à son succès : un filet
    // qui survivrait à une action réussie affirmerait un échec qui n'a plus lieu.
    setHostlessFailure(null);
    try {
      await action.run(sel);
    } catch (e) {
      await reportFailure(t('« {label} » n’a pas abouti', { label: action.label }), errorMessage(e));
    }
  }, [reportFailure]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const showFoot = shouldShowFoot(page, body);
  const sortNotice = sortNoticeLabel(columns, cols, sort);

  return (
    <div className={['dt-root', className].filter(Boolean).join(' ')} style={style} data-table={tableId}>
      {/* ⚠️ La barre existe dès qu'il y a une SÉLECTION, avec ou sans actions de masse : elle dit ce qui
          est sélectionné et comment y renoncer. La conditionner à `bulkActions` fabriquait une
          condition invisible — une table à sélection sans actions n'affichait ni son compteur, ni
          « Tout sélectionner (N) », et l'app ne pouvait pas savoir pourquoi. */}
      {showBulk && selection && (
        <div className="dt-bulk" role="region" aria-label={t('Actions sur la sélection')}>
          <strong className="dt-bulk-count">{selectionLabel(selCount, unit)}</strong>
          {/* ⚠️ La portée « au-delà de la page » n'est proposée QUE lorsqu'elle a un sens : serveur apte,
              total connu, page entièrement cochée, jeu filtré plus grand que la page. Elle émet un
              CRITÈRE — avec une pagination serveur, les identifiants des pages non chargées n'existent
              pas côté client. */}
          {offerSelectAll && (
            <button
              type="button"
              className="dt-bulk-action"
              onClick={() => selection.onChange(selectAllMatching(matchingTotal))}
            >
              
              {t('Tout sélectionner ({matchingTotal})', { matchingTotal })}</button>
          )}
          {/* ⚠️ LE POUSSEUR : compteur à gauche, actions à droite. Le «margin-left:auto« qui vivait sur
              le seul bouton de renoncement n'emportait que lui — les actions restaient collées au
              compteur, disposition inversée par rapport à la maquette. */}
          <div className="dt-bulk-push" aria-hidden />
          {bulkActions?.map((a) => {
            // ⚠️ Le refus porte sa RAISON en infobulle : un bouton gris muet est la version douce du
            // mensonge — l'écran refuse sans dire pourquoi.
            const why = a.disabled?.(selection.value) ?? false;
            const off = why !== false || frozen;
            const title = why !== false ? why : frozen ? t('Rechargement en cours') : undefined;
            return (
              <button
                key={a.id}
                type="button"
                className={`dt-bulk-action${a.tone === 'danger' ? ' danger' : ''}`}
                aria-disabled={off || undefined}
                title={title}
                // ⚠️ Gelé pendant un rechargement : laisser partir une action de masse sur une sélection
                // dont le jeu sous-jacent est en train de changer, c'est agir sur des lignes qu'on ne
                // voit plus.
                onClick={() => { if (!off) void runBulkAction(a, selection.value); }}
              >
                {a.label}
              </button>
            );
          })}
          {/* ⚠️ LE RENONCEMENT N'EST PAS UNE ACTION DE PLUS : seul élément à fond de la barre, carré,
              sans libellé. Habillé comme les autres, il se lisait comme une action — alors qu'il défait
              la sélection qui a fait apparaître la barre. Son nom accessible reste écrit en toutes lettres. */}
          <button
            type="button"
            className="dt-bulk-clear"
            aria-label={t('Annuler la sélection')}
            onClick={() => selection.onChange(clearSelection())}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      )}

      {/* ⚠️ La densité descend en VARIABLES posées sur la carte, pas en hauteur écrite dans chaque
          rangée : dans la maquette `--row-h` était bien câblé mais les deux tableaux principaux
          écrivaient 56 px et 42 px en dur — le réglage de densité était décoratif. */}
      <div className="dt-card" style={densityVars(density, rowHeight) as CSSProperties}>
        {/* ⚠️ Le filet de dernier recours : sans `<DialogHost/>`, l'alerte de charte n'écrit qu'en
            console, et l'écran d'un échec redeviendrait celui d'un succès. */}
        {hostlessFailure && (
          <div className="dt-action-error" role="alert">{hostlessFailure}</div>
        )}
        {(toolbar || onColumnLayoutChange || onExport) && (
          <div className="dt-toolbar">
            {toolbar}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {onColumnLayoutChange && (
                <ColumnsButton
                  columns={columns.map((c) => ({ key: c.key, label: c.label, locked: c.locked, hideable: isColumnHideable(c), pinned: c.pinned }))}
                  layout={reconcileColumnLayout(columns.map((c) => c.key), columnLayout ?? null)}
                  onChange={onColumnLayoutChange}
                  reorderable={reorderableColumns}
                  persistenceNote={columnPersistenceNote}
                />
              )}
              {onExport && (
                <button
                  type="button"
                  className="btn-ghost sm"
                  // ⚠️ Le composant ne produit AUCUN fichier et ne lit aucune ligne : il remonte la
                  // DESCRIPTION de la vue, et le serveur produit le classeur sur le jeu filtré entier.
                  // Dans la maquette, tous les boutons d'export n'affichent qu'un bandeau qui AFFIRME
                  // le succès sans qu'aucun fichier ne soit écrit (§23.0).
                  // ⚠️ Un échec de l'app est RENDU VISIBLE ici : sans ce `catch`, un export raté
                  // laissait l'écran identique à un export réussi.
                  onClick={() => {
                    const view = buildTableView(columns, columnLayout, sort, page ?? { index: 0, size: rows.length, total: 'unknown' });
                    setHostlessFailure(null);
                    void (async () => {
                      try {
                        await onExport(view);
                      } catch (e) {
                        await reportFailure(t('L’export n’a pas abouti'), errorMessage(e));
                      }
                    })();
                  }}
                >
                  {t(EXPORT_LABEL)}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ⚠️ Les filtres appliqués se répètent en chips RÉVOCABLES : « un filtre caché rend l'écran
            menteur » — on lit « 6 pièces », on en conclut une journée calme, un filtre traînait
            derrière un panneau. Sur des écrans adossés au fiscal, un total incomplet pris pour
            complet est un risque réel. */}
        {filterChips && filterChips.length > 0 && (
          <div className="dt-chips">
            {filterChips.map((c) => (
              <button key={c.id} type="button" className="chip active" onClick={c.onRemove} title={t('Retirer ce filtre')}>
                {c.label} ✕
              </button>
            ))}
            {onClearFilters && (
              <button type="button" className="btn-link" onClick={onClearFilters}>{t('Effacer tout')}</button>
            )}
          </div>
        )}

        <div
          ref={scrollRef}
          className="dt-scroll"
          data-sticky={sticky ? 'on' : 'off'}
          aria-busy={frozen || undefined}
          style={bounded ? { maxHeight: maxHeight ?? TABLE.scroll.maxHeight } : undefined}
        >
          {/* ⚠️ LES COLONNES SURVIVENT AUX DEUX VIDES (décision utilisateur 2026-08-29) : un état
              vide qui efface la structure fait disparaître ce que l'écran EST — le message se lit
              DANS le tableau, sous ses en-têtes, comme la maquette. Erreur / chargement / attente
              restent pleins : des colonnes au-dessus d'une panne prétendraient un tableau sain. */}
          {(body === 'rows' || body === 'empty' || body === 'noMatch') && (
            <table className="dt" style={floor > 0 ? ({ '--dt-min-w': `${floor}px` } as CSSProperties) : undefined}>
              {/* ⚠️ Le `<colgroup>` est DÉRIVÉ des colonnes visibles : une colonne soumise à droit n'est
                  simplement pas passée dans `columns` — « il n'est pas grisé, il n'existe pas » — et
                  aucune piste vide ne peut donc subsister. */}
              <colgroup>
                {selection && <col style={{ width: TABLE.gutter.check + TABLE.row.padH }} />}
                {expandable && <col style={{ width: TABLE.gutter.expand + TABLE_EXPAND_LEAD }} />}
                {/* ⚠️ `width:100%` sur la colonne SANS socle : en largeur adaptative, l'excédent est
                    réparti au PRORATA et aucune colonne n'absorbe seule. Une seule est promue — la
                    première déclarée sans largeur — sinon deux se partagent le reliquat. */}
                {cols.map((c) => (
                  <col key={c.key} style={c.width ? { width: c.width } : c.key === growKey ? { width: '100%' } : undefined} />
                ))}
              </colgroup>
              <thead className="dt-head">
                <tr>
                  {selection && (
                    <th scope="col" {...gutterCell('check', t('Sélection'))}>
                      <CheckBox
                        checked={pageAllSelected ? true : pageSomeSelected ? 'mixed' : false}
                        label={t('Sélectionner les lignes de cette page')}
                        onToggle={togglePage}
                      />
                      {/* ⚠️ VISIBLE UNIQUEMENT EN CARTE (< 768 px), où la rangée d'en-tête se réduit à
                          cette seule cellule : une case à cocher seule au-dessus d'une pile de cartes ne
                          dit pas ce qu'elle coche. En bureau, l'intitulé de colonne joue ce rôle et la
                          charte masque ce libellé — le rendre des deux côtés doublerait l'en-tête. */}
                      <span className="dt-head-card-label">{t('Tout sélectionner')}</span>
                    </th>
                  )}
                  {expandable && <th scope="col" {...gutterCell('expand', t('Détail'))} aria-label={t('Déplier')} />}
                  {cols.map((c) => {
                    const active = sort?.key === c.key;
                    // ⚠️ UNE SEULE condition pour la classe, `aria-sort` et le bouton : `sortable` sans
                    // gestionnaire produisait un en-tête annoncé triable qui ne triait rien.
                    const sortableHere = isColumnSortable(c, canSort);
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        title={c.headerTitle}
                        aria-sort={ariaSortOf(c, sort, canSort)}
                        style={columnSizeVars(c)}
                        className={[sortableHere ? 'sortable' : '', c.numeric ? 'dt-num' : '', active ? 'active' : ''].filter(Boolean).join(' ')}
                      >
                        {sortableHere && onSortChange ? (
                          <button
                            type="button"
                            className={`th-sort${active ? ' active' : ''}`}
                            onClick={() => onSortChange(nextSort(c, sort))}
                          >
                            {c.label}
                            <span className="arrow">{active ? (sort?.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {body === 'rows' && (
              <tbody>
                {rows.map((row) => {
                  const id = getRowId(row);
                  const tone = rowTone?.(row);
                  const isSel = selected(row);
                  const why = selection?.selectable?.(row);
                  const expanded = expandable?.expandedIds.has(id) ?? false;
                  return (
                    <Fragment key={id}>
                      <tr
                        data-row-id={id}
                        className={[
                          'dt-row',
                          isSel ? 'selected' : '',
                          tone ?? '',
                          onRowActivate ? 'clickable' : '',
                          focusRowId === id ? 'focused' : '',
                        ].filter(Boolean).join(' ')}
                        // ⚠️ PRÉCÉDENCE IMPOSÉE PAR LE COMPOSANT — case, puis chevron, puis ligne :
                        // trois cibles vivent dans une même rangée cliquable et, sans ordre imposé,
                        // cocher une ligne l'ouvrirait. Ce qui dépend de la vigilance de l'appelant
                        // finit toujours par diverger (§24.3bis B-2). Les deux premières branches
                        // arrêtent la propagation à la source, sur leur propre gestionnaire.
                        onClick={() => onRowActivate?.(row)}
                      >
                        {/* ⚠️ `data-gutter` et NON `data-card="off"` : ces deux cellules RESTENT en mode
                            carte. Masquées, elles retiraient sélection multiple et dépliage de la
                            surface même pour laquelle on pose des cibles tactiles de 44 px. */}
                        {selection && (
                          <td {...gutterCell('check', t('Sélection'))}>
                            <CheckBox
                              checked={isSel}
                              disabledReason={why !== true && typeof why === 'string' ? why : undefined}
                              label={t('Sélectionner {name}', { name: unitOne })}
                              onToggle={() => toggleRow(row)}
                            />
                          </td>
                        )}
                        {expandable && (
                          <td {...gutterCell('expand', t('Détail'))}>
                            <button
                              type="button"
                              className="dt-expand"
                              aria-expanded={expanded}
                              aria-label={expanded ? t('Replier') : t('Déplier')}
                              onClick={(e) => { e.stopPropagation(); expandable.onToggle(id); }}
                            >
                              {/* ⚠️ UN TRACÉ, PAS UN GLYPHE — la même correction que la coche de
                                  sélection (2026-08-16). Le caractère « ▸ » rendu à 10 px dépendait de
                                  la police de repli du système : sous Windows il bascule sur Segoe UI
                                  Symbol, et le chevron n’avait ni la même épaisseur ni la même
                                  position sur la ligne de base que partout ailleurs.
                                  ⚠️ DEUX CHEMINS DISTINCTS, PAS UNE ROTATION (relevé maquette l.941) :
                                  un chevron tourné à 90° pointe en biais pendant toute la transition,
                                  et son trait paraît plus épais en diagonale qu’à l’horizontale. */}
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d={expanded ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                              </svg>
                            </button>
                          </td>
                        )}
                        {cols.map((c, i) => (
                          <td
                            key={c.key}
                            // ⚠️ Attributs de carte produits par `cardCellAttrs`, la MÊME recette que
                            // la ligne de total : c'est la seule façon qu'une colonne masquée en carte
                            // le soit aussi dans son total.
                            {...cardCellAttrs(c)}
                            className={['dt-cell', c.numeric ? 'dt-num' : '', c.mono ? 'dt-mono' : ''].filter(Boolean).join(' ')}
                            style={{
                              ...columnSizeVars(c),
                              ...(c.align && !c.numeric ? { textAlign: c.align === 'end' ? 'right' : c.align === 'center' ? 'center' : 'left' } : null),
                            }}
                          >
                            {i === 0 && onRowActivate ? (
                              <button type="button" className="dt-row-open" onClick={(e) => { e.stopPropagation(); onRowActivate(row); }}>
                                {c.render(row)}
                              </button>
                            ) : (
                              c.render(row)
                            )}
                          </td>
                        ))}
                      </tr>
                      {expandable && expanded && (
                        <tr className="dt-row">
                          {/* ⚠️ Une cellule à `colSpan` DIMENSIONNE les colonnes qu'elle couvre : sans
                              l'enveloppe ci-dessous, déplier une ligne redimensionnerait tout le
                              tableau et le replier le remettrait en place — les colonnes sauteraient
                              sous les yeux à chaque chevron. */}
                          <td className="dt-cell" data-label="Détail" data-expand-body colSpan={cols.length + (selection ? 1 : 0) + (expandable ? 1 : 0)}>
                            <div className="dt-expand-body">{expandable.render(row)}</div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              )}
              {body === 'rows' && totalRow && (() => {
                /**
                 * OÙ COMMENCE LE PREMIER TOTAL — c'est lui qui dit s'il reste de la place à gauche
                 * pour poser la note sur la même ligne.
                 * ⚠️ `-1` (aucun total chiffré) ou `0` (total en première colonne) ⇒ pas de place :
                 * la note reprend sa rangée. Un chiffre serré contre son explication se lit moins
                 * bien que deux lignes franches.
                 */
                const iPremierTotal = cols.findIndex((c) => totalRow.cells[c.key] != null);
                const noteEnLigne = Boolean(totalRow.note) && iPremierTotal > 0;
                return (
                <tfoot>
                  <tr className="dt-total">
                    {/* ⚠️ `data-gutter` ici AUSSI : ces cellules sont VIDES dans un total, et en mode
                        carte chaque cellule devient une ligne — non marquées, elles ouvraient deux
                        lignes vides sous le total. Le corps avait été corrigé, le pied oublié. */}
                    {selection && <td {...gutterCell('check', t('Sélection'))} />}
                    {expandable && <td {...gutterCell('expand', t('Détail'))} />}
                    {/* ⚠️ `dt-cell` et attributs de carte comme dans le corps, par la MÊME recette :
                        sans elle, une colonne `card:false` disparaissait du corps sous 768 px mais son
                        TOTAL restait affiché, intitulé compris — un chiffre que l'écran ne sait plus
                        qualifier. */}
                    {/* ⚠️ LA NOTE OCCUPE LES COLONNES LAISSÉES VIDES À GAUCHE DU PREMIER TOTAL, sur la
                        MÊME ligne : « 36 pièces · 1 avoir déduit » et « 4 800,00 € » forment une seule
                        phrase, et la couper en deux rangées faisait lire deux informations distinctes.
                        ⚠️ Repli en rangée séparée quand il n'y a PAS de place — total en première
                        colonne, ou colonnes toutes occupées : mieux vaut deux lignes qu'un chiffre
                        écrasé contre son explication. */}
                    {noteEnLigne && (
                      <td className="dt-total-note-cell" colSpan={(selection ? 1 : 0) + (expandable ? 1 : 0) + iPremierTotal}>
                        {totalRow.note}
                      </td>
                    )}
                    {cols.map((c, i) => (noteEnLigne && i < iPremierTotal ? null : (
                      <td key={c.key} {...cardCellAttrs(c)} className={['dt-cell', c.numeric ? 'dt-num' : ''].filter(Boolean).join(' ')}>
                        {totalRow.cells[c.key] ?? null}
                      </td>
                    )))}
                  </tr>
                  {/* La note DIT ce que le total exclut. Acceptée puis jetée, elle laissait présenter une
                      somme partielle comme complète. */}
                  {totalRow.note && !noteEnLigne && (
                    <tr className="dt-total-note">
                      <td colSpan={cols.length + (selection ? 1 : 0) + (expandable ? 1 : 0)}>{totalRow.note}</td>
                    </tr>
                  )}
                </tfoot>
                );
              })()}
            </table>
          )}

          {/* ⚠️ AUCUN SQUELETTE : le chargement se DIT. Un squelette dessine des lignes qui n'existent
              pas et prétend en connaître le nombre. */}
          {body === 'loading' && <div className="dt-state">{t('Chargement…')}</div>}

          {/* ⚠️ « En attente » et non « Chargement… » : aucune requête n'est partie. Nommer une
              opération qui n'a pas commencé est une étape sans action derrière (§23.0). */}
          {body === 'idle' && <div className="dt-state idle">{t('En attente')}</div>}

          {/* ⚠️ Le message RÉEL de l'exception, tel quel — jamais une phrase rassurante réécrite — et
              AUCUNE ligne rendue : afficher les anciennes sous une erreur les ferait croire à jour. */}
          {body === 'error' && status.phase === 'error' && (
            <div role="alert" className="dt-error">
              <div className="dt-error-title">{t('Échec du chargement')}</div>
              <pre className="dt-error-msg">{status.message}</pre>
              {status.detail && <div className="dt-error-note">{status.detail}</div>}
              {status.onRetry && (
                <button type="button" className="btn-ghost sm" style={{ marginTop: 12 }} onClick={status.onRetry}>{t('Réessayer')}</button>
              )}
            </div>
          )}

          {body === 'empty' && (
            <EmptyState icon={emptyStates.empty.icon} title={emptyStates.empty.title} note={emptyStates.empty.note} action={emptyStates.empty.action} />
          )}

          {/* ⚠️ État vide DISTINCT du précédent : « un état vide qui ne dit pas comment en sortir est un
              cul-de-sac ». Confondre les deux fait dire « aucun article » à un catalogue de 30 000
              références filtré sur une famille vide. */}
          {body === 'noMatch' && (
            <EmptyState
              icon="search"
              title={emptyStates.noMatch?.title ?? t('Aucun résultat')}
              note={emptyStates.noMatch?.note ?? t('Les filtres actifs ne ramènent aucune ligne.')}
              action={onClearFilters ? (
                <button type="button" className="btn-ghost sm" onClick={onClearFilters}>
                  {emptyStates.noMatch?.clearLabel ?? t('Effacer les filtres')}
                </button>
              ) : undefined}
            />
          )}
        </div>

        {/* ⚠️ Le pied SURVIT AUX VIDES dès qu'on n'est pas en première page (sinon une page hors bornes
            devient un cul-de-sac : « aucune ligne » alors qu'il y en a ailleurs, et plus aucun contrôle
            pour y aller) — mais JAMAIS sous une erreur, un chargement ou une attente, où il affirmerait
            une plage de lignes que le corps refuse de rendre. */}
        {showFoot && page && (
          <div className="dt-foot">
            <span className="dt-mono">{pageLabel(page, rows.length)}</span>
            {sortNotice && <span>{sortNotice}</span>}
            {frozen && <span>{t('Mise à jour…')}</span>}
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              {onPageSizeChange && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{t('Par page')}</span>
                  <select
                    className="field-opt"
                    value={page.size}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  >
                    {pageSizeOptions(page.size).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              )}
              {/* ⚠️ De VRAIS `disabled` : dans la maquette les flèches grisées restent cliquables et
                  focalisables — un bouton qui fait semblant d'être éteint.
                  ⚠️ Et `onPageChange` compte AUTANT que les bornes : `page` et le gestionnaire sont deux
                  props indépendantes, donc une table peut annoncer sa pagination sans savoir en changer.
                  Rendues actives, les flèches avalaient alors le clic en silence — le même défaut que
                  `sortable` sans gestionnaire, corrigé par `isColumnSortable`. La pagination était la
                  dernière exception. */}
              <button type="button" className="pg-btn" disabled={!canGoToPage(page, rows.length, !!onPageChange, 'prev')} aria-label={t('Page précédente')} onClick={() => onPageChange?.(page.index - 1)}>‹</button>
              <button type="button" className="pg-btn" disabled={!canGoToPage(page, rows.length, !!onPageChange, 'next')} aria-label={t('Page suivante')} onClick={() => onPageChange?.(page.index + 1)}>›</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Case à cocher du tableau — la boîte peinte est un span, le bouton ne porte que la cible (44 px au
 * tactile).
 * ⚠️ C'est `aria-checked` qui porte l'état de sélection, et lui seul : `aria-selected` posé sur le
 * `<tr>` d'une `table` simple n'est admis que dans une `grid`/`treegrid` — les lecteurs d'écran
 * l'ignorent, la teinte de ligne restait alors une information purement visuelle.
 */
function CheckBox({ checked, onToggle, label, disabledReason }: {
  checked: boolean | 'mixed';
  onToggle: () => void;
  label: string;
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      className="dt-check"
      aria-checked={checked === 'mixed' ? 'mixed' : checked}
      aria-label={label}
      aria-disabled={disabledReason ? true : undefined}
      title={disabledReason}
      onClick={(e) => { e.stopPropagation(); if (!disabledReason) onToggle(); }}
    >
      {/* ⚠️ UN TRACÉ, PAS UN GLYPHE (correctif 2026-08-16). La coche était le caractère « ✓ » rendu à
          11 px : son dessin, son épaisseur et sa position sur la ligne de base dépendaient de la police
          de repli du système (sous Windows il bascule sur Segoe UI Symbol) — la marque n'était donc pas
          la même d'un poste à l'autre, et elle était nettement plus fine que celle de la maquette.
          ⚠️ ÉTAT INDÉTERMINÉ : AJOUT sur la maquette, pas un relevé. Elle n'en a aucun (sa case d'en-tête
          est un booléen `rows.every`) et signale la sélection partielle par la seule barre d'actions.
          Une case vide sur une page à moitié cochée dit le faux ; le tiret dit « partiel ». */}
      <span className="dt-box" aria-hidden>
        {checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
            <path d={checked === 'mixed' ? 'M6 12h12' : 'M20 6L9 17l-5-5'} />
          </svg>
        )}
      </span>
    </button>
  );
}
